import { useEffect, useState } from 'react';
import {
  getExperimentVariantOverride,
  readExperimentVariant,
} from './posthogExperiment';

// PostHog A/B experiment on the discount countdown's reach (ORA-892). The
// webinar thank-you page already counts down to the School Launch deadline;
// this test puts the same urgency on the surfaces a launch-cohort student
// sees on every visit. Variants:
//   control -> in-app discount banner and pricing page as today (static
//              percent-off, no countdown)
//   test    -> a live countdown to the current phase's deadline (PRIMARY,
//              then STEP_DOWN) on the banner and in the pricing page header
export const DISCOUNT_COUNTDOWN_EXPERIMENT_FLAG = 'discount-countdown-surfaces';

export type DiscountCountdownVariant = 'control' | 'test';

const VALID_VARIANTS = ['control', 'test'] as const;

// Local/QA override via `?dcVariant=control|test`. When present it bypasses
// PostHog entirely (no flag read, so no real exposure), which lets each arm be
// forced locally — where PostHog never initializes — and on deployed previews
// without polluting experiment results.
export const getDiscountCountdownVariantOverride =
  (): DiscountCountdownVariant | null =>
    getExperimentVariantOverride('dcVariant', VALID_VARIANTS);

// The override when there is one, else the PostHog variant — which FIRES the
// exposure event (`$feature_flag_called`), so callers only invoke it once a
// countdown surface is genuinely on screen for a user the experiment applies
// to. Falls back to `control` outside production, when PostHog is disabled
// (EU), or on any error.
export const readDiscountCountdownVariant = async (
  userId: string
): Promise<DiscountCountdownVariant> =>
  getDiscountCountdownVariantOverride() ??
  readExperimentVariant({
    flag: DISCOUNT_COUNTDOWN_EXPERIMENT_FLAG,
    validVariants: VALID_VARIANTS,
    fallback: 'control',
    userId,
  });

// The banner (shell level) and the pricing page can be on screen together, so
// one read per user per page load is shared between them: the second surface
// joins the in-flight promise instead of identifying and waiting again. Keyed
// per user so an account switch (or admin impersonation) in the same page
// load neither reuses another user's assignment nor re-reads the first one.
const sharedReads = new Map<string, Promise<DiscountCountdownVariant>>();

const readSharedVariant = (
  userId: string
): Promise<DiscountCountdownVariant> => {
  let promise = sharedReads.get(userId);

  if (!promise) {
    promise = readDiscountCountdownVariant(userId);
    sharedReads.set(userId, promise);
  }

  return promise;
};

export const resetDiscountCountdownVariantForTests = () => {
  sharedReads.clear();
};

/**
 * Resolves the variant once the surface is eligible: `enabled` is the
 * surface's own "I am showing a School Launch discount with a future deadline
 * to an authed user" condition, so nobody is bucketed before the countdown
 * could have been shown. `null` until resolved — surfaces render the control
 * state in that window. The first successful read is final for the mount
 * and user, so a late discount change can't flip the surface mid-view; a
 * different user id (account switch, impersonation) starts over from `null`.
 */
export const useDiscountCountdownVariant = ({
  userId,
  enabled,
}: {
  userId: string | null | undefined;
  enabled: boolean;
}): DiscountCountdownVariant | null => {
  const [resolved, setResolved] = useState<{
    userId: string;
    variant: DiscountCountdownVariant;
  } | null>(null);

  const variant =
    resolved && userId && resolved.userId === userId ? resolved.variant : null;

  useEffect(() => {
    if (variant !== null || !enabled || !userId) return;

    let isMounted = true;

    void readSharedVariant(userId).then((value) => {
      if (isMounted) setResolved({ userId, variant: value });
    });

    return () => {
      isMounted = false;
    };
  }, [variant, enabled, userId]);

  return variant;
};
